/**
 * @license Copyright (c) 2003-2019, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md.
 */

/* eslint-env browser */
'use strict';

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

import mix from '@ckeditor/ckeditor5-utils/src/mix';
import ObservableMixin from '@ckeditor/ckeditor5-utils/src/observablemixin';
import CKEditorError from '@ckeditor/ckeditor5-utils/src/ckeditorerror';
var DEFAULT_OPTIONS = {
  refreshInterval: 3600000,
  autoRefresh: true
};
/**
 * Class representing the token used for communication with CKEditor Cloud Services.
 * Value of the token is retrieving from the specified URL and is refreshed every 1 hour by default.
 *
 * @mixes ObservableMixin
 */

var Token = /*#__PURE__*/function () {
  /**
   * Creates `Token` instance.
   * Method `init` should be called after using the constructor or use `create` method instead.
   *
   * @param {String|Function} tokenUrlOrRefreshToken Endpoint address to download the token or a callback that provides the token. If the
   * value is a function it has to match the {@link ~refreshToken} interface.
   * @param {Object} options
   * @param {String} [options.initValue] Initial value of the token.
   * @param {Number} [options.refreshInterval=3600000] Delay between refreshes. Default 1 hour.
   * @param {Boolean} [options.autoRefresh=true] Specifies whether to start the refresh automatically.
   */
  function Token(tokenUrlOrRefreshToken) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_OPTIONS;

    _classCallCheck(this, Token);

    if (!tokenUrlOrRefreshToken) {
      /**
       * A `tokenUrl` must be provided as the first constructor argument.
       *
       * @error token-missing-token-url
       */
      throw new CKEditorError('token-missing-token-url: A `tokenUrl` must be provided as the first constructor argument.', this);
    }
    /**
     * Value of the token.
     * The value of the token is null if `initValue` is not provided or `init` method was not called.
     * `create` method creates token with initialized value from url.
     *
     * @name value
     * @member {String} #value
     * @observable
     * @readonly
     */


    this.set('value', options.initValue);
    /**
     * Base refreshing function.
     *
     * @private
     * @member {String|Function} #_refresh
     */

    if (typeof tokenUrlOrRefreshToken === 'function') {
      this._refresh = tokenUrlOrRefreshToken;
    } else {
      this._refresh = function () {
        return defaultRefreshToken(tokenUrlOrRefreshToken);
      };
    }
    /**
     * @type {Object}
     * @private
     */


    this._options = Object.assign({}, DEFAULT_OPTIONS, options);
  }
  /**
   * Initializes the token.
   *
   * @returns {Promise.<Token>}
   */


  _createClass(Token, [{
    key: "init",
    value: function init() {
      var _this = this;

      return new Promise(function (resolve, reject) {
        if (_this._options.autoRefresh) {
          _this._startRefreshing();
        }

        if (!_this.value) {
          _this._refreshToken().then(resolve)["catch"](reject);

          return;
        }

        resolve(_this);
      });
    }
    /**
     * Refresh token method. Useful in a method form as it can be override in tests.
     *
     * @protected
     */

  }, {
    key: "_refreshToken",
    value: function _refreshToken() {
      var _this2 = this;

      return this._refresh().then(function (value) {
        return _this2.set('value', value);
      }).then(function () {
        return _this2;
      });
    }
    /**
     * Destroys token instance. Stops refreshing.
     */

  }, {
    key: "destroy",
    value: function destroy() {
      this._stopRefreshing();
    }
    /**
     * Starts value refreshing every `refreshInterval` time.
     *
     * @protected
     */

  }, {
    key: "_startRefreshing",
    value: function _startRefreshing() {
      var _this3 = this;

      this._refreshInterval = setInterval(function () {
        return _this3._refreshToken();
      }, this._options.refreshInterval);
    }
    /**
     * Stops value refreshing.
     *
     * @protected
     */

  }, {
    key: "_stopRefreshing",
    value: function _stopRefreshing() {
      clearInterval(this._refreshInterval);
    }
    /**
     * Creates a initialized {@link Token} instance.
     *
     * @param {String|Function} tokenUrlOrRefreshToken Endpoint address to download the token or a callback that provides the token. If the
     * value is a function it has to match the {@link ~refreshToken} interface.
     * @param {Object} options
     * @param {String} [options.initValue] Initial value of the token.
     * @param {Number} [options.refreshInterval=3600000] Delay between refreshes. Default 1 hour.
     * @param {Boolean} [options.autoRefresh=true] Specifies whether to start the refresh automatically.
     * @returns {Promise.<Token>}
     */

  }], [{
    key: "create",
    value: function create(tokenUrlOrRefreshToken) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_OPTIONS;
      var token = new Token(tokenUrlOrRefreshToken, options);
      return token.init();
    }
  }]);

  return Token;
}();

mix(Token, ObservableMixin);
/**
 * This function is called in a defined interval by the {@link ~Token} class.
 * It should return a promise, which resolves with the new token value.
 * If any error occurs it should return a rejected promise with an error message.
 *
 * @function refreshToken
 * @returns {Promise.<String>}
 */

/**
 * @private
 * @param {String} tokenUrl
 */

function defaultRefreshToken(tokenUrl) {
  return new Promise(function (resolve, reject) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', tokenUrl);
    xhr.addEventListener('load', function () {
      var statusCode = xhr.status;
      var xhrResponse = xhr.response;

      if (statusCode < 200 || statusCode > 299) {
        /**
         * Cannot download new token from the provided url.
         *
         * @error token-cannot-download-new-token
         */
        return reject(new CKEditorError('token-cannot-download-new-token: Cannot download new token from the provided url.', null));
      }

      return resolve(xhrResponse);
    });
    xhr.addEventListener('error', function () {
      return reject(new Error('Network Error'));
    });
    xhr.addEventListener('abort', function () {
      return reject(new Error('Abort'));
    });
    xhr.send();
  });
}

export default Token;